import { dropTask } from 'ember-concurrency';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import Input from 'tio-reimagined/components/input';
import RadioGroup from 'tio-reimagined/components/radio-group';
import Form from 'tio-reimagined/components/form';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack } from 'tio-ui/components/layout';
import * as v from 'valibot';
import Component from '@glimmer/component';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import inputmask from 'tio-common/modifiers/inputmask';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import type { FormResult } from 'tio-reimagined/components/form';
import { array, fn, hash } from '@ember/helper';
import type { IntlService } from 'ember-intl';
import type Owner from '@ember/owner';
import type PslfFormModel from 'tio-data/models/pslf-form';
import type { NotificationsService } from 'tio-reimagined/components/notifications';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';

type ChangeApproverFormData = {
  employerEmail?: string;
  employerContactName?: string;
  employerContactTitle?: string;
  employerContactPhone?: string;
};

export interface PslfMyDocumentsMyFormsTableChangeApproverSignature {
  Args: {
    form: PslfFormModel;
    didSave: (form: PslfFormModel) => Promise<void>;
    didCancel: () => void;
  };
}

export default class PslfMyDocumentsMyFormsTableChangeApproverComponent extends Component<PslfMyDocumentsMyFormsTableChangeApproverSignature> {
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare store: StoreService;

  @tracked formData: Partial<{
    employerEmail: string;
    employerContactName: string;
    employerContactTitle: string;
    employerContactPhone: string;
  }> = {};
  @tracked borrowerPrintAndMail = false;
  @tracked errors: Record<string, string[]> = {};
  @tracked hasSubmitted = false;

  get hasEmail() {
    return !this.borrowerPrintAndMail;
  }

  get formSchema() {
    if (this.borrowerPrintAndMail) {
      // No validation required when printing and mailing
      return v.object({
        employerEmail: v.optional(v.string(), ''),
        employerContactName: v.optional(v.string(), ''),
        employerContactTitle: v.optional(v.string(), ''),
        employerContactPhone: v.optional(v.string(), ''),
      });
    }

    return v.object({
      employerEmail: v.pipe(
        v.string('Email is required'),
        v.nonEmpty('Email is required'),
        v.email('Please enter a valid email address.')
      ),
      employerContactName: v.optional(v.string(), ''),
      employerContactTitle: v.optional(v.string(), ''),
      employerContactPhone: v.pipe(
        v.string(),
        v.check((input) => {
          const digits = input.replace(/\D/g, '');
          if (digits.length === 0 || digits === '1') return true;
          return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
        }, 'Please enter a valid US phone number.')
      ),
    });
  }

  constructor(owner: Owner, args: PslfMyDocumentsMyFormsTableChangeApproverSignature['Args']) {
    super(owner, args);
    this.initializeFormData();
    this.borrowerPrintAndMail = this.args.form.borrowerFields['borrower-print-and-mail'] || false;
  }

  removeCountryCodePrefix = (phone: string | undefined) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return digits.slice(1);
    }
    return digits;
  };

  initializeFormData = () => {
    const editableForm = this.args.form;

    const phoneNumber = editableForm.employerFields['employer-contact-phone-number'] || '';
    this.formData = {
      employerEmail: editableForm.employerFields['employer-contact-email'] || '',
      employerContactName: editableForm.employerFields['employer-contact-name'] || '',
      employerContactTitle: editableForm.employerFields['employer-contact-title'] || '',
      employerContactPhone: this.removeCountryCodePrefix(phoneNumber),
    };
  };

  printAndMailDidChange = (value: string) => {
    this.borrowerPrintAndMail = value === 'true';
    if (value !== 'true') {
      this.initializeFormData();
    }
    this.errors = {};
  };

  onInputChange = (field: keyof ChangeApproverFormData, value: string) => {
    this.formData = { ...this.formData, [field]: value };
    this.onFormChange({ data: this.formData, isValid: false, errors: {} });
  };

  onFormChange = (result: FormResult<ChangeApproverFormData>) => {
    const formResultData = v.safeParse(this.formSchema, result.data);
    // @ts-expect-error: valibot types
    this.formData = { ...this.formData, ...formResultData.output };

    if (formResultData.success) {
      this.errors = {};
    } else {
      const validationErrors: Record<string, string[]> = {};
      for (const issue of formResultData.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.errors = validationErrors;
    }
  };

  onFormSubmit = (result: FormResult<ChangeApproverFormData>) => {
    this.hasSubmitted = true;
    this.onFormChange(result);
    const errorKeys = Object.keys(this.errors);
    if (errorKeys.length === 0) {
      // Form is valid, proceed with submission
      this.submitTask.perform();
    }
  };

  submitTask = dropTask(async () => {
    this.hasSubmitted = true;
    if (Object.keys(this.errors).length) {
      return;
    }
    try {
      const saved = await this.saveFormValues.perform();
      this.hasSubmitted = false;
      this.args.didSave?.(saved);
      this.args.didCancel();
    } catch (e) {
      console.error(e);
      this.notifications.add('Something went wrong', { appearance: 'error' });
    }
  });

  getFieldValue = (fieldValue: string | undefined) => {
    return this.borrowerPrintAndMail ? '' : fieldValue || '';
  };

  cleanPhoneNumber = (phone: string | undefined) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 0 || digits === '1') return '';
    return digits;
  };

  saveFormValues = dropTask(async () => {
    const { form } = this.args;

    const updatedEmployerFields = {
      ...form.employerFields,
      employerContactEmail: this.getFieldValue(this.formData.employerEmail),
      employerContactName: this.getFieldValue(this.formData.employerContactName),
      employerContactTitle: this.getFieldValue(this.formData.employerContactTitle),
      employerContactPhoneNumber: this.borrowerPrintAndMail
        ? ''
        : this.cleanPhoneNumber(this.formData.employerContactPhone),
    };
    // TODO: normalize casing to camelCase
    const formattedEmployerFields: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(updatedEmployerFields)) {
      const camelKey = key.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
      formattedEmployerFields[camelKey] = value;
    }
    // @ts-expect-error: employer fields casing
    form.employerFields = formattedEmployerFields;

    const updatedBorrowerFields = {
      ...form.borrowerFields,
      borrowerPrintAndMail: this.borrowerPrintAndMail,
    };
    const formattedBorrowerFields: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(updatedBorrowerFields)) {
      const camelKey = key.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
      formattedBorrowerFields[camelKey] = value;
    }
    // @ts-expect-error: borrower fields casing
    form.borrowerFields = formattedBorrowerFields;

    await form.save();

    if (this.borrowerPrintAndMail) {
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      await this.store.adapterFor('pslf-form').transferPdf(form);
    }

    return form;
  });

  <template>
    <Modal
      {{! TODO: should be tracking this in the parent... }}
      @isOpen={{true}}
      @onClose={{@didCancel}}
      class="min-h-64 md:min-w-2xl"
    >
      <:header>{{t "pslf.workflow_prior_employer.send_your_document"}}</:header>

      <:body>
        <Form
          @data={{this.formData}}
          @onChange={{this.onFormChange}}
          @onSubmit={{this.onFormSubmit}}
        >
          <VStack>
            <RadioGroup
              data-test-id="send-electronically-radio-group"
              @label={{t "pslf.workflow_prior_employer.send_electronically"}}
              @value={{if this.borrowerPrintAndMail "true" "false"}}
              @onChange={{this.printAndMailDidChange}}
              @options={{array
                (hash
                  key="false"
                  label=(t "pslf.workflow_prior_employer.i_have_email")
                  dataTestId="radio-have-email"
                )
                (hash
                  key="true"
                  label=(t "pslf.workflow_prior_employer.i_dont_have_email")
                  dataTestId="radio-dont-have-email"
                )
              }}
            />

            {{#if this.hasEmail}}
              <div class="mt-4">
                <p class="text-lg font-medium py-2">
                  {{t "pslf.workflow_prior_employer.please_provide"}}
                </p>
                <p class="text-sm text-tio-gray-600">
                  {{t "pslf.workflow_prior_employer.please_provide_hint"}}
                </p>
              </div>

              <AlertBanner @intent="info">
                <p class="text-sm">
                  {{t "pslf.workflow_prior_employer.correct_email_alert"}}
                </p>
              </AlertBanner>

              <Input
                data-test-id="employer-email-input"
                @name="employerEmail"
                @label={{t "pslf.workflow_prior_employer.employer_email"}}
                @value={{this.formData.employerEmail}}
                @onChange={{fn this.onInputChange "employerEmail"}}
                @description={{t "pslf.workflow_prior_employer.employer_email_hint"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employerEmail"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
              />

              <Input
                data-test-id="employer-contact-name-input"
                @name="employerContactName"
                @label={{t "pslf.workflow_prior_employer.contact_name"}}
                @value={{this.formData.employerContactName}}
                @onChange={{fn this.onInputChange "employerContactName"}}
                @description={{t "pslf.workflow_prior_employer.optional_not_required"}}
                @errors={{errorsForValibotField
                  "employerContactName"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
              />

              <Input
                data-test-id="employer-contact-title-input"
                @name="employerContactTitle"
                @label={{t "pslf.workflow_prior_employer.contact_title"}}
                @value={{this.formData.employerContactTitle}}
                @onChange={{fn this.onInputChange "employerContactTitle"}}
                @description={{t "pslf.workflow_prior_employer.optional"}}
                @errors={{errorsForValibotField
                  "employerContactTitle"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
              />

              <Input
                data-test-id="employer-contact-phone-input"
                @name="employerContactPhone"
                @label={{t "pslf.workflow_prior_employer.contact_phone"}}
                @value={{this.formData.employerContactPhone}}
                @onChange={{fn this.onInputChange "employerContactPhone"}}
                @description={{t "pslf.workflow_prior_employer.optional"}}
                @errors={{errorsForValibotField
                  "employerContactPhone"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                {{inputmask alias="+1 (999) 999-9999"}}
              />
            {{/if}}
          </VStack>
        </Form>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.submitTask.isRunning}}
          @onSubmit={{this.submitTask.perform}}
          @submitText={{if
            this.borrowerPrintAndMail
            (t "common.save")
            (t "pslf.workflow_prior_employer.submit_for_approval")
          }}
          @onCancel={{@didCancel}}
        />
      </:footer>
    </Modal>
  </template>
}
